import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import {
  getInboxScrollTop,
  setInboxScrollTop,
} from "@/mobile/lib/mobileInboxScrollMemory";

const PULL_THRESHOLD = 72;
const MAX_PULL = 112;

type Props = {
  children: ReactNode;
  onRefresh: () => void;
  refreshing: boolean;
  className?: string;
  /** Restaure la position scroll au remontage (retour lecteur). */
  scrollMemoryKey?: string;
  /** Re-tente la restauration quand la liste change de taille. */
  scrollRestoreDep?: number;
  /** Bloque scroll + pull (lecteur message ouvert). */
  scrollLocked?: boolean;
  /** Ref vers le conteneur scroll (ancrage ligne). */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
};

/** Pull-to-refresh mobile — barre horizontale (plus de spinner circulaire). */
export function MobilePullToRefresh({
  children,
  onRefresh,
  refreshing,
  className,
  scrollMemoryKey,
  scrollRestoreDep = 0,
  scrollLocked = false,
  scrollContainerRef,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const setScrollNode = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      if (scrollContainerRef) {
        (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [scrollContainerRef]
  );
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const pullRef = useRef(0);
  const suppressUntilRef = useRef(0);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);

  const setPullDistance = useCallback((value: number) => {
    pullRef.current = value;
    setPull(value);
  }, []);

  const resetPull = useCallback(() => {
    setPullDistance(0);
    setDragging(false);
    pullingRef.current = false;
  }, [setPullDistance]);

  useEffect(() => {
    if (!refreshing) {
      resetPull();
    }
  }, [refreshing, resetPull]);

  /** Après fermeture du tiroir : ignore le PTR le temps que le geste se termine. */
  useEffect(() => {
    if (scrollLocked) {
      resetPull();
      suppressUntilRef.current = Number.POSITIVE_INFINITY;
      return;
    }
    suppressUntilRef.current = Date.now() + 480;
    const t = window.setTimeout(() => {
      suppressUntilRef.current = 0;
    }, 500);
    return () => window.clearTimeout(t);
  }, [scrollLocked, resetPull]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !scrollMemoryKey) return;

    const saved = getInboxScrollTop(scrollMemoryKey);
    if (saved == null || saved <= 0) return;

    const apply = () => {
      if (el.scrollHeight <= saved + 8) return false;
      el.scrollTop = saved;
      return true;
    };

    if (apply()) return;

    const observer = new ResizeObserver(() => {
      apply();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollMemoryKey, scrollRestoreDep]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !scrollMemoryKey) return;

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setInboxScrollTop(scrollMemoryKey, el.scrollTop);
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      setInboxScrollTop(scrollMemoryKey, el.scrollTop);
      el.removeEventListener("scroll", onScroll);
    };
  }, [scrollMemoryKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || scrollLocked) return;

    const isSuppressed = () => Date.now() < suppressUntilRef.current;
    const canPull = () => el.scrollTop <= 0;

    const onTouchStart = (e: TouchEvent) => {
      if (refreshing || isSuppressed() || !canPull()) return;
      startYRef.current = e.touches[0]?.clientY ?? 0;
      pullingRef.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (refreshing || isSuppressed()) return;

      const y = e.touches[0]?.clientY ?? 0;
      const delta = y - startYRef.current;

      if (!pullingRef.current) {
        if (delta <= 0 || !canPull()) return;
        pullingRef.current = true;
        setDragging(true);
      }

      if (delta > 0) {
        e.preventDefault();
        setPullDistance(Math.min(delta * 0.45, MAX_PULL));
      } else {
        setPullDistance(0);
        pullingRef.current = false;
        setDragging(false);
      }
    };

    const onTouchEnd = () => {
      if (refreshing || isSuppressed()) {
        resetPull();
        return;
      }

      setDragging(false);
      const current = pullRef.current;

      if (current >= PULL_THRESHOLD) {
        setPullDistance(PULL_THRESHOLD * 0.35);
        onRefreshRef.current();
      } else {
        setPullDistance(0);
      }
      pullingRef.current = false;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [refreshing, scrollLocked, setPullDistance, resetPull]);

  const showBar = !scrollLocked && pull > 0;
  const pullProgress = Math.min(pull / PULL_THRESHOLD, 1);
  const ready = pull >= PULL_THRESHOLD;

  return (
    <div className={cn("relative min-h-0 flex-1", className)}>
      <div
        className={cn(
          "mobile-ptr-bar pointer-events-none absolute inset-x-0 top-0 z-10 overflow-hidden transition-opacity duration-150",
          showBar ? "opacity-100" : "opacity-0"
        )}
        style={{
          height: showBar ? Math.max(3, 3 + pull * 0.08) : 0,
        }}
        aria-hidden={!showBar}
      >
        <div className="absolute inset-x-0 bottom-0 h-[3px] bg-[var(--mail-border-soft)]">
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-75",
              ready ? "bg-[var(--mail-accent)]" : "bg-[var(--mail-accent)]/55"
            )}
            style={{ width: `${Math.max(8, pullProgress * 100)}%` }}
          />
        </div>
      </div>

      <div
        ref={setScrollNode}
        className={cn(
          "mobile-ptr-scroll h-full overscroll-y-contain",
          scrollLocked ? "overflow-hidden" : "overflow-y-auto"
        )}
        style={{
          transform: pull > 0 && !scrollLocked ? `translateY(${pull}px)` : undefined,
          transition: dragging ? undefined : "transform 0.22s ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );
}
